import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { PDFDocument, rgb } from "pdf-lib";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf";
import { db, storage } from "../services/firebase";

GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const INK_COLOR = "#2563eb";
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DEFAULT_STROKE_WIDTH = 2.5;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getPointFromClient(clientX, clientY, element) {
  const rect = element.getBoundingClientRect();
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
}

function distanceBetweenTouches(t1, t2) {
  const dx = t1.x - t2.x;
  const dy = t1.y - t2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function cloneStrokesMap(map) {
  return Object.fromEntries(
    Object.entries(map || {}).map(([key, strokes]) => [
      key,
      (strokes || []).map((stroke) => ({
        ...stroke,
        points: (stroke.points || []).map((point) => ({ ...point })),
      })),
    ]),
  );
}

export default function DocketSignModal({
  open,
  jobId,
  docket,
  documents = [],
  documentIndex = 0,
  documentCount = 1,
  onDocumentChange,
  driverEmail,
  onClose,
  onSigned,
}) {
  const baseCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const documentViewportRef = useRef(null);
  const pdfDocRef = useRef(null);
  const pdfBytesRef = useRef(null);
  const pageViewportSizesRef = useRef({});
  const touchesRef = useRef({});
  const pinchRef = useRef({
    active: false,
    startDistance: 0,
    startScale: 1,
    startTranslate: { x: 0, y: 0 },
    center: null,
  });
  const drawRef = useRef({ active: false, pointerId: null, strokeId: null });
  const panRef = useRef({
    active: false,
    pointerId: null,
    startClient: null,
    startTranslate: { x: 0, y: 0 },
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [signedName, setSignedName] = useState("");
  const [mode, setMode] = useState("draw");
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfReady, setPdfReady] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [strokesByPage, setStrokesByPage] = useState({});

  const isPdf = useMemo(
    () =>
      /pdf/i.test(docket?.contentType || "") ||
      /\.pdf$/i.test(docket?.name || ""),
    [docket?.contentType, docket?.name],
  );
  const isViewOnly = docket?.mode === "view";
  const isImage = useMemo(
    () =>
      /^image\//i.test(docket?.contentType || "") ||
      /\.(png|jpe?g|gif|webp|bmp)$/i.test(docket?.name || ""),
    [docket?.contentType, docket?.name],
  );

  const currentPageKey = String(pageNumber);
  const currentPdfStrokes = strokesByPage[currentPageKey] || [];

  const hasAnyMarks = useMemo(
    () =>
      Object.values(strokesByPage).some(
        (strokes) => (strokes || []).length > 0,
      ),
    [strokesByPage],
  );

  useEffect(() => {
    if (!open) return;
    setLoading(false);
    setSaving(false);
    setError("");
    setSignedName("");
    setMode(isViewOnly ? "move" : "draw");
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setPageCount(1);
    setPageNumber(1);
    setPdfReady(false);
    setCanvasSize({ width: 0, height: 0 });
    setStrokesByPage({});
    touchesRef.current = {};
    pdfDocRef.current = null;
    pdfBytesRef.current = null;
    pageViewportSizesRef.current = {};
  }, [open, docket?.url, isViewOnly]);

  useEffect(() => {
    if (!open || !docket?.url) return undefined;
    if (!isPdf) {
      if (isViewOnly && isImage) {
        setLoading(false);
        setError("");
        return undefined;
      }
      setError(
        isViewOnly
          ? "This viewer supports PDF and image documents only."
          : "Documents requiring a signature must be PDFs.",
      );
      return undefined;
    }

    let cancelled = false;

    async function loadPdf() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(docket.url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const bytes = await res.arrayBuffer();
        if (cancelled) return;

        // Keep one untouched copy for pdf-lib save step
        pdfBytesRef.current = bytes.slice(0);

        // Give pdf.js its own copy
        const loadingTask = getDocument({
          data: bytes.slice(0),
          disableWorker: true,
        });

        const pdf = await loadingTask.promise;
        if (cancelled) return;

        pdfDocRef.current = pdf;
        setPageCount(pdf.numPages || 1);
        setPageNumber(1);
        setPdfReady(true);
      } catch (err) {
        console.error("Failed to load docket preview:", err);
        if (!cancelled) {
          setError(
            `Failed to load docket preview: ${err?.message || String(err)}`,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [open, docket?.url, isPdf, isImage, isViewOnly]);

  useEffect(() => {
    if (!open || !isPdf || !pdfReady || !pdfDocRef.current) return undefined;

    let cancelled = false;

    async function renderPdfPage() {
      const canvas = baseCanvasRef.current;
      if (!canvas) return;

      try {
        setLoading(true);
        setError("");
        const page = await pdfDocRef.current.getPage(pageNumber);
        if (cancelled) return;

        const unscaledViewport = page.getViewport({ scale: 1 });
        const viewer = documentViewportRef.current;
        const availableWidth = Math.max(
          (viewer?.clientWidth || unscaledViewport.width) - 32,
          1,
        );
        const availableHeight = Math.max(
          (viewer?.clientHeight || unscaledViewport.height) - 32,
          1,
        );
        const fitScale = Math.min(
          availableWidth / unscaledViewport.width,
          availableHeight / unscaledViewport.height,
        );
        const viewport = page.getViewport({ scale: fitScale });
        const outputScale = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const context = canvas.getContext("2d");
        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
        context.clearRect(0, 0, viewport.width, viewport.height);
        await page.render({ canvasContext: context, viewport }).promise;
        if (cancelled) return;

        pageViewportSizesRef.current[pageNumber] = {
          width: viewport.width,
          height: viewport.height,
        };
        setCanvasSize({ width: viewport.width, height: viewport.height });
      } catch (err) {
        console.error("Failed to render PDF page:", err);
        if (!cancelled) {
          setError(`Failed to render PDF page: ${err?.message || String(err)}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    renderPdfPage();
    return () => {
      cancelled = true;
    };
  }, [open, isPdf, pageNumber, pdfReady]);

  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas || !canvasSize.width || !canvasSize.height) return;

    const outputScale = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = Math.floor(canvasSize.width * outputScale);
    canvas.height = Math.floor(canvasSize.height * outputScale);
    canvas.style.width = `${canvasSize.width}px`;
    canvas.style.height = `${canvasSize.height}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(outputScale, 0, 0, outputScale, 0, 0);
    redrawOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasSize, pageNumber, strokesByPage]);

  function redrawOverlay() {
    const canvas = overlayCanvasRef.current;
    if (!canvas || !canvasSize.width || !canvasSize.height) return;

    const outputScale = Math.max(window.devicePixelRatio || 1, 1);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(outputScale, 0, 0, outputScale, 0, 0);
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    currentPdfStrokes.forEach((stroke) => {
      const points = stroke.points || [];
      if (!points.length) return;

      ctx.beginPath();
      ctx.strokeStyle = stroke.color || INK_COLOR;
      ctx.lineWidth = stroke.width || DEFAULT_STROKE_WIDTH;
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i += 1) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      if (points.length === 1) {
        ctx.lineTo(points[0].x + 0.01, points[0].y + 0.01);
      }
      ctx.stroke();
    });
  }

  function clientToDocPoint(clientX, clientY) {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return null;

    const { x, y } = getPointFromClient(clientX, clientY, canvas);
    return {
      x: x / scale,
      y: y / scale,
    };
  }

  function appendStrokePoint(strokeId, point) {
    setStrokesByPage((prev) => ({
      ...prev,
      [currentPageKey]: (prev[currentPageKey] || []).map((stroke) =>
        stroke.id === strokeId
          ? { ...stroke, points: [...stroke.points, point] }
          : stroke,
      ),
    }));
  }

  function startStroke(clientX, clientY, pointerId) {
    if (mode !== "draw") return;
    const point = clientToDocPoint(clientX, clientY);
    if (!point) return;

    const stroke = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      color: INK_COLOR,
      width: DEFAULT_STROKE_WIDTH,
      points: [point],
    };

    drawRef.current = {
      active: true,
      pointerId,
      strokeId: stroke.id,
    };

    setStrokesByPage((prev) => ({
      ...prev,
      [currentPageKey]: [...(prev[currentPageKey] || []), stroke],
    }));
  }

  function startPan(clientX, clientY, pointerId) {
    panRef.current = {
      active: true,
      pointerId,
      startClient: { x: clientX, y: clientY },
      startTranslate: { ...translate },
    };
  }

  function handlePointerDown(evt) {
    if (evt.pointerType === "touch") return;
    if (evt.button !== 0) return;
    evt.preventDefault();
    setError("");

    if (mode === "draw") {
      startStroke(evt.clientX, evt.clientY, evt.pointerId);
    } else {
      startPan(evt.clientX, evt.clientY, evt.pointerId);
    }
  }

  function handlePointerMove(evt) {
    if (drawRef.current.active && drawRef.current.pointerId === evt.pointerId) {
      evt.preventDefault();
      const point = clientToDocPoint(evt.clientX, evt.clientY);
      if (!point) return;
      appendStrokePoint(drawRef.current.strokeId, point);
      return;
    }

    if (panRef.current.active && panRef.current.pointerId === evt.pointerId) {
      evt.preventDefault();
      const dx = evt.clientX - panRef.current.startClient.x;
      const dy = evt.clientY - panRef.current.startClient.y;
      setTranslate({
        x: panRef.current.startTranslate.x + dx,
        y: panRef.current.startTranslate.y + dy,
      });
    }
  }

  function handlePointerUp(evt) {
    if (drawRef.current.pointerId === evt.pointerId) {
      drawRef.current = { active: false, pointerId: null, strokeId: null };
    }
    if (panRef.current.pointerId === evt.pointerId) {
      panRef.current = {
        active: false,
        pointerId: null,
        startClient: null,
        startTranslate: { x: 0, y: 0 },
      };
    }
  }

  function handleTouchStart(evt) {
    const nextTouches = { ...touchesRef.current };
    Array.from(evt.changedTouches).forEach((touch) => {
      nextTouches[touch.identifier] = { x: touch.clientX, y: touch.clientY };
    });
    touchesRef.current = nextTouches;

    const ids = Object.keys(nextTouches);
    if (ids.length === 2) {
      const t1 = nextTouches[ids[0]];
      const t2 = nextTouches[ids[1]];
      pinchRef.current = {
        active: true,
        startDistance: distanceBetweenTouches(t1, t2),
        startScale: scale,
        startTranslate: { ...translate },
        center: { x: (t1.x + t2.x) / 2, y: (t1.y + t2.y) / 2 },
      };
      drawRef.current = { active: false, pointerId: null, strokeId: null };
      panRef.current = {
        active: false,
        pointerId: null,
        startClient: null,
        startTranslate: { x: 0, y: 0 },
      };
      return;
    }

    if (ids.length === 1) {
      const touch = evt.changedTouches[0];
      setError("");
      if (mode === "draw") {
        startStroke(touch.clientX, touch.clientY, touch.identifier);
      } else {
        startPan(touch.clientX, touch.clientY, touch.identifier);
      }
    }
  }

  function handleTouchMove(evt) {
    const nextTouches = { ...touchesRef.current };
    Array.from(evt.changedTouches).forEach((touch) => {
      nextTouches[touch.identifier] = { x: touch.clientX, y: touch.clientY };
    });
    touchesRef.current = nextTouches;

    const ids = Object.keys(nextTouches);
    if (ids.length === 2 && pinchRef.current.active) {
      evt.preventDefault();
      const t1 = nextTouches[ids[0]];
      const t2 = nextTouches[ids[1]];
      const nextDistance = distanceBetweenTouches(t1, t2);
      const nextScale = clamp(
        (nextDistance / pinchRef.current.startDistance) *
          pinchRef.current.startScale,
        MIN_SCALE,
        MAX_SCALE,
      );
      setScale(nextScale);

      const currentCenter = {
        x: (t1.x + t2.x) / 2,
        y: (t1.y + t2.y) / 2,
      };
      setTranslate({
        x:
          pinchRef.current.startTranslate.x +
          (currentCenter.x - pinchRef.current.center.x),
        y:
          pinchRef.current.startTranslate.y +
          (currentCenter.y - pinchRef.current.center.y),
      });
      return;
    }

    if (evt.changedTouches.length !== 1) return;
    const touch = evt.changedTouches[0];

    if (
      drawRef.current.active &&
      drawRef.current.pointerId === touch.identifier
    ) {
      evt.preventDefault();
      const point = clientToDocPoint(touch.clientX, touch.clientY);
      if (!point) return;
      appendStrokePoint(drawRef.current.strokeId, point);
      return;
    }

    if (
      panRef.current.active &&
      panRef.current.pointerId === touch.identifier
    ) {
      evt.preventDefault();
      const dx = touch.clientX - panRef.current.startClient.x;
      const dy = touch.clientY - panRef.current.startClient.y;
      setTranslate({
        x: panRef.current.startTranslate.x + dx,
        y: panRef.current.startTranslate.y + dy,
      });
    }
  }

  function handleTouchEnd(evt) {
    const nextTouches = { ...touchesRef.current };
    Array.from(evt.changedTouches).forEach((touch) => {
      delete nextTouches[touch.identifier];
      if (drawRef.current.pointerId === touch.identifier) {
        drawRef.current = { active: false, pointerId: null, strokeId: null };
      }
      if (panRef.current.pointerId === touch.identifier) {
        panRef.current = {
          active: false,
          pointerId: null,
          startClient: null,
          startTranslate: { x: 0, y: 0 },
        };
      }
    });
    touchesRef.current = nextTouches;
    if (Object.keys(nextTouches).length < 2) {
      pinchRef.current.active = false;
    }
  }

  function resetView() {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }

  function changeDocument(nextIndex) {
    if (nextIndex < 0 || nextIndex >= documentCount) return;
    if (hasAnyMarks && !isViewOnly) {
      const discard = window.confirm(
        "Your unsaved marks on this document will be cleared. Continue?",
      );
      if (!discard) return;
    }
    onDocumentChange?.(nextIndex);
  }

  function clearCurrentPage() {
    setStrokesByPage((prev) => ({ ...prev, [currentPageKey]: [] }));
  }

  function undoLastStroke() {
    setStrokesByPage((prev) => ({
      ...prev,
      [currentPageKey]: (prev[currentPageKey] || []).slice(0, -1),
    }));
  }

  async function buildSignedPdfBlob() {
    if (!pdfBytesRef.current) throw new Error("PDF docket not ready.");
    const pdfDoc = await PDFDocument.load(pdfBytesRef.current.slice(0));
    const pages = pdfDoc.getPages();
    const strokesMap = cloneStrokesMap(strokesByPage);

    for (let index = 0; index < pages.length; index += 1) {
      const page = pages[index];
      const strokes = strokesMap[String(index + 1)] || [];
      if (!strokes.length) continue;

      const { width, height } = page.getSize();
      const renderPage = await pdfDocRef.current.getPage(index + 1);
      const viewport =
        pageViewportSizesRef.current[index + 1] ||
        renderPage.getViewport({ scale: 1 });
      const scaleX = width / viewport.width;
      const scaleY = height / viewport.height;
      const thicknessScale = (scaleX + scaleY) / 2;

      strokes.forEach((stroke) => {
        const points = stroke.points || [];
        if (!points.length) return;

        if (points.length === 1) {
          page.drawCircle({
            x: points[0].x * scaleX,
            y: height - points[0].y * scaleY,
            size: 1.1 * thicknessScale,
            color: rgb(37 / 255, 99 / 255, 235 / 255),
            opacity: 1,
          });
          return;
        }

        for (let i = 0; i < points.length - 1; i += 1) {
          const p1 = points[i];
          const p2 = points[i + 1];
          page.drawLine({
            start: { x: p1.x * scaleX, y: height - p1.y * scaleY },
            end: { x: p2.x * scaleX, y: height - p2.y * scaleY },
            thickness: (stroke.width || DEFAULT_STROKE_WIDTH) * thicknessScale,
            color: rgb(37 / 255, 99 / 255, 235 / 255),
            opacity: 1,
          });
        }
      });
    }

    const bytes = await pdfDoc.save();
    return new Blob([bytes], { type: "application/pdf" });
  }

  async function saveSignedDocket() {
    if (isViewOnly) return;
    if (!jobId || !docket?.url) return;
    if (!hasAnyMarks) {
      setError("Please draw on the PDF before saving.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const blob = await buildSignedPdfBlob();
      const storagePath = `signedDockets/${jobId}/signed-${Date.now()}.pdf`;
      await uploadBytes(ref(storage, storagePath), blob, {
        contentType: "application/pdf",
      });
      const url = await getDownloadURL(ref(storage, storagePath));

      const signedResult = {
        url,
        storagePath,
        name: `${jobId}-signed.pdf`,
        contentType: "application/pdf",
        signedByName: signedName.trim() || "",
        signedByDriver: driverEmail || "",
        signedAt: serverTimestamp(),
        sourceName: docket?.name || "",
      };
      const jobUpdate = {
        signedDocketUrl: url,
        signedDocketStoragePath: storagePath,
        signedDocketFileName: `${jobId}-signed.pdf`,
        signedDocketContentType: "application/pdf",
        signedDocketSignedByName: signedName.trim() || "",
        signedDocketSignedByDriver: driverEmail || "",
        signedDocketSignedAt: serverTimestamp(),
      };

      if (docket?.id && docket.id !== "legacy") {
        jobUpdate[`signedJobDocuments.${docket.id}`] = signedResult;
      }

      await updateDoc(doc(db, "jobs", jobId), jobUpdate);

      onSigned?.({
        url,
        storagePath,
        contentType: "application/pdf",
        name: `${jobId}-signed.pdf`,
        documentId: docket?.id || "legacy",
      });
      if (documentIndex < documentCount - 1) {
        onDocumentChange?.(documentIndex + 1);
      } else {
        onClose?.();
      }
    } catch (err) {
      console.error("Failed to save signed docket:", err);
      setError(`Failed to save signed docket: ${err?.message || String(err)}`);
    } finally {
      setSaving(false);
    }
  }

  if (!open || !docket?.url) return null;

  const modal = (
    <div className="fixed inset-0 z-[80] bg-black/60 p-3 sm:p-6">
      <div className="mx-auto flex h-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {isViewOnly ? "Job instructions" : "Customer docket signing"}
            </h3>
            <p className="text-xs text-gray-500">
              {isViewOnly
                ? "Review this document before attending the location. Use two fingers to zoom."
                : "Draw directly on the PDF. Use two fingers to zoom. Switch to Move mode to pan around."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {documentCount > 1 ? (
              <>
                <select
                  value={documentIndex}
                  onChange={(event) => changeDocument(Number(event.target.value))}
                  className="max-w-[180px] rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700"
                  aria-label="Choose job document"
                >
                  {documents.map((item, index) => (
                    <option key={item.id || index} value={index}>
                      {index + 1}. {item.name || `Document ${index + 1}`}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => changeDocument(documentIndex - 1)}
                  disabled={documentIndex <= 0}
                  className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="whitespace-nowrap text-xs font-medium text-gray-600">
                  {documentIndex + 1} of {documentCount}
                </span>
                <button
                  type="button"
                  onClick={() => changeDocument(documentIndex + 1)}
                  disabled={documentIndex >= documentCount - 1}
                  className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(60vh,1fr)_auto] overflow-y-auto lg:grid-cols-[1.55fr_0.85fr] lg:grid-rows-1 lg:overflow-hidden">
          <div className="flex min-h-0 flex-col border-b border-gray-200 bg-gray-100 lg:border-b-0 lg:border-r">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs text-gray-600">
              <span className="truncate">
                {docket?.name || "Uploaded docket"}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                {!isViewOnly ? (
                  <button
                    type="button"
                    onClick={() => setMode("draw")}
                    className={`rounded border px-2 py-1 ${
                      mode === "draw"
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-gray-300 bg-white hover:bg-gray-50"
                    }`}
                  >
                    Draw
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setMode("move")}
                  className={`rounded border px-2 py-1 ${
                    mode === "move"
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white hover:bg-gray-50"
                  }`}
                >
                  Move
                </button>
                {!isViewOnly ? (
                  <>
                    <button
                      type="button"
                      onClick={undoLastStroke}
                      className="rounded border border-gray-300 bg-white px-2 py-1 hover:bg-gray-50"
                    >
                      Undo
                    </button>
                    <button
                      type="button"
                      onClick={clearCurrentPage}
                      className="rounded border border-gray-300 bg-white px-2 py-1 hover:bg-gray-50"
                    >
                      Clear page
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={resetView}
                  className="rounded border border-gray-300 bg-white px-2 py-1 hover:bg-gray-50"
                >
                  Reset view
                </button>
                <a
                  href={docket.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded border border-gray-300 bg-white px-2 py-1 hover:bg-gray-50"
                >
                  Open original
                </a>
              </div>
            </div>

            {isPdf ? (
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 text-xs text-gray-600">
              <button
                type="button"
                onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                disabled={pageNumber <= 1}
                className="rounded border border-gray-300 bg-white px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous page
              </button>
              <span>
                Page {pageNumber} / {pageCount}
              </span>
              <button
                type="button"
                onClick={() =>
                  setPageNumber((prev) => Math.min(pageCount, prev + 1))
                }
                disabled={pageNumber >= pageCount}
                className="rounded border border-gray-300 bg-white px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next page
              </button>
            </div>
            ) : null}

            <div className="relative min-h-0 flex-1 overflow-hidden">
              <div
                ref={documentViewportRef}
                className="absolute inset-0 overflow-auto touch-none"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
              >
                <div className="flex min-h-full min-w-full items-start justify-center p-4">
                  <div
                    className="relative max-w-full origin-top-left bg-white shadow"
                    style={{
                      transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                      width: canvasSize.width
                        ? `${canvasSize.width}px`
                        : "auto",
                      height: canvasSize.height
                        ? `${canvasSize.height}px`
                        : "auto",
                    }}
                  >
                    {isPdf ? (
                      <>
                        <canvas ref={baseCanvasRef} className="block" />
                        {!isViewOnly ? (
                          <canvas
                            ref={overlayCanvasRef}
                            className="absolute left-0 top-0 block"
                          />
                        ) : null}
                      </>
                    ) : (
                      <img
                        src={docket.url}
                        alt={docket?.name || "Job instructions"}
                        className="block h-full w-full object-contain"
                        style={{
                          visibility: canvasSize.width ? "visible" : "hidden",
                        }}
                        onLoad={(event) => {
                          const image = event.currentTarget;
                          const viewer = documentViewportRef.current;
                          const availableWidth = Math.max(
                            (viewer?.clientWidth || image.naturalWidth) - 32,
                            1,
                          );
                          const availableHeight = Math.max(
                            (viewer?.clientHeight || image.naturalHeight) - 32,
                            1,
                          );
                          const fitScale = Math.min(
                            availableWidth / image.naturalWidth,
                            availableHeight / image.naturalHeight,
                            1,
                          );
                          setCanvasSize({
                            width: image.naturalWidth * fitScale,
                            height: image.naturalHeight * fitScale,
                          });
                        }}
                        onError={() =>
                          setError("Failed to load the instruction image.")
                        }
                      />
                    )}
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-sm font-medium text-gray-600">
                  Loading document...
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex min-h-0 flex-col bg-white">
            {!isViewOnly ? (
              <div className="border-b border-gray-200 px-4 py-3">
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Customer name (optional)
                </label>
                <input
                  value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  placeholder="Type customer name"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
              </div>
            ) : null}

            <div className="flex-1 space-y-4 overflow-auto p-4 text-sm text-gray-700">
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-blue-900">
                <p className="font-medium">How to use</p>
                <p className="mt-1 text-xs leading-5">
                  {isViewOnly
                    ? "Read the instructions carefully. Use Move mode to drag the document around, or pinch with two fingers to zoom."
                    : "Use Draw mode to write directly on the PDF in blue. Use Move mode to drag the document around. Pinch with two fingers to zoom in or out."}
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Current file
                </p>
                <p className="mt-1 font-medium text-gray-900">
                  {docket?.name || "Uploaded docket"}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {isPdf ? "PDF document" : "Image document"}
                </p>
              </div>

              {!isViewOnly ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Signed pages
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {
                      Object.values(strokesByPage).filter(
                        (strokes) => (strokes || []).length > 0,
                      ).length
                    }{" "}
                    of {pageCount} page(s)
                  </p>
                </div>
              ) : null}

              {error ? <p className="text-sm text-red-600">{error}</p> : null}
            </div>

            {!isViewOnly ? (
            <div className="border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={saveSignedDocket}
                disabled={saving || loading || !isPdf}
                className={`w-full rounded-xl px-4 py-2 text-sm font-medium text-white transition ${
                  saving || loading || !isPdf
                    ? "cursor-not-allowed bg-gray-400"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {saving ? "Saving signed docket..." : "Save signed PDF"}
              </button>
            </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
